// Entity nodes on the project canvas — the imperative half (spec §3.1–3.6).
//
// An entity node is a canvas item whose KEY is an entity ref string
// (`ref:<type>:<id>` — the shared contract, `el.call('parseEntityRef', …)`,
// `@symbo-ls/workspace-shared/functions/entityRef.js`). Everything else is the
// canvas you already have: the same `canvas.items[key] = { x, y, w, h }`
// geometry, the same board pages, the same selection/drag/marquee chrome, the
// same `connections` array, the same `sdk.updateProject(id, { canvas })`
// persistence. The only new pieces are here:
//
//   resolveCanvasEntity(key)      read the entity behind a ref, cache it
//   getCanvasEntity(key)          synchronous cache read (safe in a prop)
//   expandCanvasEntity(key)       relations → new nodes + connections
//   addCanvasEntityItem(ref, at)  place one node
//   focusCanvasEntity(key)        centre + select (the assistant's source chip)
//   openCanvasEntity(key)         route to the entity's own surface
//   searchCanvasEntities(query)   the picker's data source
//   boardEntityItems()            the sidebar's Items list
//   openCanvasEntityComments(key) the Comments sheet
//   makeCanvasEntityVariation(k)  duplicate a record as a variation
//
// ── NOTHING HERE IS VAULT-SPECIFIC ────────────────────────────────────────
// The package knows about ref TYPES (record/doc/mail/ticket/…) and RELATION
// names, both of which are platform vocabulary. It knows nothing about
// tenants, training records, or any collection name. A module supplies those
// through the record row's own `data` bag and through `boardPage.meta`.
//
// ── SDK ACCESS ────────────────────────────────────────────────────────────
// `el.sdk` (the boot singleton), the canvas's canonical accessor — the same
// one sessionOverlay.js / projectPublish.js / versionHistory.js use. Reads go
// through `sdk.execute(<entity>, <op>, args)`, the EntityDispatcher surface.
// Every read is feature-detected and every failure is caught: a canvas whose
// backend cannot answer must still render its nodes (with an error state on
// the card), never throw out of a lifecycle handler.
//
// ── ROUTES THIS PACKAGE CODES AGAINST BUT DOES NOT OWN ────────────────────
//   sdk.execute('refs', 'relations', { ref, depth })
//        → { ref, neighbors: [{ ref, relation, direction }] }
//   sdk.execute('search', 'workspace', { query, limit })
//        → [{ ref, title, snippet, surface }]
//
// TODO(vaultos-v2, core lane / L1): both routes are ABSENT from the SDK's
// ENTITY_ROUTES table as of 2026-09-07 — `sdk.execute` THROWS SYNCHRONOUSLY
// for an unknown entity, before it returns a promise. Nothing in this file
// needs to change when they land; the ONLY action is to repoint the
// `@symbo.ls/sdk` link so the new route table is the one that loads. Until
// then every call here resolves to an empty result (see _execute) — Expand
// reports "nothing to add", the picker shows its empty state, and the console
// stays clean. `__tests__/entitySdkDegradation.test.js` pins that against all
// three failure shapes (sync throw / rejected promise / `{ error }` envelope)
// and carries a positive control for its own console capture.

const _canvasRoot = (el) => el?.state?.root || el?.getRoot?.()?.state?.root || null
const _canvasSdk = (el) => {
  const sdk = el?.sdk || _canvasRoot(el)?.sdk || null
  return sdk && typeof sdk.execute === 'function' ? sdk : null
}

// In-flight de-duplication for resolveCanvasEntity. Module-scope mutable
// state, the same shape functions/canvas/state.js already keeps for
// `_persistTimer` / `_chromeRafId` / `previewSizeSeededProjects`: it is a
// per-tab request ledger, never rendered and never persisted, so it must NOT
// live on the reactive store (a store write per in-flight request would
// re-render the board once per node on load).
const _inflight = new Map()

// Unwrap a RESPONSE ENVELOPE — and only an envelope.
//
// ⚠ `hasOwnProperty('data')` is NOT the test. A workspace RECORD row owns its
// own `data` bag right next to `id` / `collection` / `name`, so unwrapping on
// the mere presence of the key hands every caller the record's payload with
// the row — and therefore the ROW ID — stripped off. Measured live on dev
// 2026-09-07: `sdk.execute('workspaceProject.records', 'create', …)` answered
// 201 with `{ id, collection, name, data, … }`, the old unwrap returned the
// `data` bag, `created.id` read `undefined`, and `makeCanvasEntityVariation`
// returned null — after the server had already written the row. The user saw
// nothing happen and every retry minted another orphan record.
//
// An envelope is recognised by what it does NOT carry: either an explicit
// `success` flag, or nothing outside the envelope vocabulary. A row always
// carries at least one key from its own domain, so the two can never be
// confused in either direction.
const _ENVELOPE_KEYS = [
  'data',
  'success',
  'error',
  'count',
  'total',
  'page',
  'pageSize',
  'limit',
  'offset',
  'meta',
  'nextCursor'
]
const _unwrap = (res) => {
  if (!res || typeof res !== 'object' || Array.isArray(res)) return res
  if (!Object.prototype.hasOwnProperty.call(res, 'data')) return res
  if (Object.prototype.hasOwnProperty.call(res, 'success')) return res.data
  return Object.keys(res).every((k) => _ENVELOPE_KEYS.indexOf(k) !== -1) ? res.data : res
}

// One guarded `sdk.execute`. Returns null for "this route does not exist yet"
// AND for "the call failed" — both mean the same thing to every caller here:
// render the empty/error state, never throw.
const _execute = async (sdk, entity, op, args) => {
  if (!sdk) return null
  try {
    const res = await sdk.execute(entity, op, args)
    if (res && res.error) return null
    return _unwrap(res)
  } catch (_) {
    return null
  }
}

// Pick the first non-empty string among candidates. Used to fold the many
// title-ish field names the four entity surfaces use (name / title / subject)
// into one card field without a per-type branch at every read site.
const _first = (...vals) => {
  for (const v of vals) {
    if (typeof v === 'string' && v.trim()) return v.trim()
    if (typeof v === 'number') return String(v)
  }
  return ''
}

// Clamp a summary to something a 3-line card can hold without the layout
// deciding for us. 240 chars ≈ 3 lines at the card's width; the card still
// clamps visually with -webkit-line-clamp, this only stops a 40 KB document
// body travelling into the AI attachment payload (spec §3.8 caps).
const _clip = (text, max) => {
  const s = String(text || '')
    .replace(/\s+/g, ' ')
    .trim()
  return s.length > max ? s.slice(0, max - 1) + '…' : s
}

/**
 * normalizeCanvasEntity(ref, row) — PURE. The ONE mapping from a backend row
 * to the fields the card, the sidebar and the AI attachment all read.
 *
 * Every surface stores its own shape, so the fold happens once, here, rather
 * than at each of the three read sites. `data` is carried through untouched so
 * a module can read its own fields off the same cache entry.
 */
export const normalizeCanvasEntity = (ref, row) => {
  const r = row || {}
  const data = (r.data && typeof r.data === 'object' ? r.data : r) || {}
  const title = _first(
    data.title,
    r.title,
    r.name,
    r.subject,
    data.name,
    data.subject,
    ref && ref.title,
    ref && ref.id
  )
  return {
    type: (ref && ref.type) || '',
    id: (ref && ref.id) || '',
    title,
    // `kind` is the record's own classification when it has one; otherwise the
    // ref TYPE, which is a code identifier and stays untranslated (glossary
    // rule 5) rather than being routed through polyglot as UI copy.
    kind: _first(data.kind, r.kind, ref && ref.kind, ref && ref.type),
    status: _first(data.status, r.status, r.state),
    effectiveDate: _first(data.effectiveDate, r.effectiveDate, data.date, r.date),
    // A record ref is SELF-DESCRIBING: its id is '<collectionKey>/<recordId>',
    // so the collection is derivable from the ref alone. The shared
    // `parseEntityRef` does not split it out (it is a canvas-side reading of
    // a record id, not part of the ref grammar), so the split happens here —
    // which also keeps the meta row correct when the row read FAILED and
    // there is no `r.collection` to fall back on.
    collection: _first(
      r.collection,
      ref && ref.collection,
      ref && ref.type === 'record' && String(ref.id || '').indexOf('/') > 0
        ? String(ref.id).slice(0, String(ref.id).indexOf('/'))
        : '',
      data.tenantId,
      r.folder
    ),
    summary: _clip(
      _first(data.summary, r.summary, data.snippet, r.snippet, data.body, r.body, r.preview),
      240
    ),
    sample: !!(data.sample || r.sample),
    authority: typeof data.authority === 'number' ? data.authority : null,
    data
  }
}

// Read ONE entity from whichever service owns its type. Returns the raw row or
// null. Split out of resolveCanvasEntity so the per-type routing is readable
// and testable on its own.
const _readEntity = async (sdk, ref) => {
  if (!sdk || !ref) return null
  if (ref.type === 'record') {
    // `<collectionKey>/<recordId>` — the collection is part of the ref so the
    // node is self-describing, but the records `get` route keys on the row id
    // alone (EntityDispatcher `workspaceProject.records` → `records.get`).
    const slash = ref.id.indexOf('/')
    const id = slash > 0 ? ref.id.slice(slash + 1) : ref.id
    const collection = slash > 0 ? ref.id.slice(0, slash) : undefined
    const row = await _execute(sdk, 'workspaceProject.records', 'get', { id })
    return row && collection && !row.collection ? { ...row, collection } : row
  }
  if (ref.type === 'doc') return _execute(sdk, 'docs', 'get', { id: ref.id })
  if (ref.type === 'ticket') return _execute(sdk, 'tickets', 'get', { id: ref.id })
  if (ref.type === 'mail') {
    // The mail service is not on the EntityDispatcher yet. Feature-detect both
    // shapes rather than assuming either: a bare service method (`sdk.mail
    // .thread`) or a dispatcher route. A miss is not an error — the card falls
    // back to the ref's own title.
    const threadId = ref.id.split('#')[0]
    if (sdk.mail && typeof sdk.mail.thread === 'function') {
      try {
        const res = await sdk.mail.thread(threadId)
        return (res && res.data) || res || null
      } catch (_) {
        return null
      }
    }
    return _execute(sdk, 'mail.threads', 'get', { id: threadId })
  }
  return null
}

// el.call('getCanvasEntity', key) — SYNCHRONOUS cache read.
//
// Safe inside a reactive prop: it only reads. The loader below is the only
// writer, and it is driven from a lifecycle handler (CvEntityCard.onRender),
// never from a render effect — writing the store while an effect is reading it
// is the classic re-entrancy loop this split exists to prevent.
export function getCanvasEntity(key) {
  const v2 = this.call('getCanvasV2')
  return (v2 && v2.entities && v2.entities[key]) || null
}

// Merge one entry into `canvas.entities`.
//
// `preventUpdate: true` on purpose: this cache is DATA, not a render trigger.
// Every consumer that needs to paint the result already owns local state for
// it (the card, the sidebar row), so a store-wide re-render per resolved node
// would cost one full board reconcile per node on load and buy nothing.
export function patchCanvasEntity(key, patch) {
  const v2 = this.call('getCanvasV2')
  if (!v2 || !key) return null
  const prev = (v2.entities && v2.entities[key]) || {}
  const next = { ...prev, ...patch }
  this.call(
    'patchCanvas',
    { entities: { ...(v2.entities || {}), [key]: next } },
    {
      preventUpdate: true
    }
  )
  return next
}

// el.call('resolveCanvasEntity', key) → Promise<entry>
//
// entry = { loading, data, error } — the shape the deliverable names, with
// `data` holding the normalizeCanvasEntity fold. Resolves from cache on a
// second call, de-duplicates concurrent calls for the same key, and NEVER
// rejects (a card must render something even when the backend is unreachable).
export async function resolveCanvasEntity(key) {
  const el = this
  const ref = el.call('parseEntityRef', key)
  if (!ref) return { loading: false, data: null, error: 'ref' }
  const cached = el.call('getCanvasEntity', key)
  if (cached && !cached.loading && (cached.data || cached.error)) return cached
  const pending = _inflight.get(key)
  if (pending) return pending
  const run = (async () => {
    el.call('patchCanvasEntity', key, { loading: true, error: null })
    const sdk = _canvasSdk(el)
    const row = await _readEntity(sdk, ref)
    // A ref we cannot read is still a node: it renders with the id as its
    // title and an error flag, so the board never shows a blank card.
    const entry = row
      ? { loading: false, data: normalizeCanvasEntity(ref, row), error: null }
      : { loading: false, data: normalizeCanvasEntity(ref, null), error: 'unresolved' }
    el.call('patchCanvasEntity', key, entry)
    _inflight.delete(key)
    return entry
  })()
  _inflight.set(key, run)
  return run
}

// el.call('addCanvasEntityItem', refOrKey, at?) → the item key, or null.
//
// `at` is a CANVAS-space point (the drop point, or the viewport centre when
// the picker adds one). The node is centred on it. An entity already on the
// board is focused instead of duplicated — a board may hold a ref once.
export function addCanvasEntityItem(refOrKey, at) {
  const key = this.call('formatEntityRef', refOrKey)
  if (!key) return null
  const v2 = this.call('getCanvasV2')
  if (!v2) return null
  if (v2.items && v2.items[key]) {
    this.call('focusCanvasEntity', key)
    return key
  }
  const W = 280
  const H = 160
  let point = at
  if (!point || typeof point.x !== 'number') {
    const surf = this.getRoot?.()?.lookdown?.('CanvasSurface')
    const rect = surf?.node?.getBoundingClientRect?.()
    point = rect
      ? this.call('screenToCanvas', rect.width / 2, rect.height / 2)
      : { x: v2.viewport?.x || 0, y: v2.viewport?.y || 0 }
  }
  const items = {
    ...(v2.items || {}),
    [key]: { x: Math.round(point.x - W / 2), y: Math.round(point.y - H / 2), w: W, h: H }
  }
  this.call('patchCanvas', { items })
  this.call('selectOne', key)
  this.call('refreshCanvasChrome')
  this.call('persistCanvasLayout')
  this.call('resolveCanvasEntity', key)
  return key
}

// el.call('focusCanvasEntity', key) — centre the node in the viewport and
// select it, WITHOUT changing the zoom. Registered in the eager slice so the
// assistant's source chips can re-focus a canvas node from any surface.
//
// Deliberately not `fitToSelection`: that re-zooms to fit, which on a single
// 280×160 node throws the user to the clamp ceiling and loses their board
// context. Centring at the live scale is what "go to this node" means.
export function focusCanvasEntity(key) {
  const v2 = this.call('getCanvasV2')
  const item = v2 && v2.items && v2.items[key]
  if (!item) return false
  this.call('selectOne', key)
  const surf = this.getRoot?.()?.lookdown?.('CanvasSurface')
  const rect = surf?.node?.getBoundingClientRect?.()
  const scale = v2.viewport && v2.viewport.scale != null ? v2.viewport.scale : null
  if (rect && scale) {
    this.call('applyViewport', {
      x: rect.width / 2 - (item.x + item.w / 2) * scale,
      y: rect.height / 2 - (item.y + item.h / 2) * scale,
      scale
    })
  } else {
    this.call('fitToSelection')
  }
  this.call('refreshCanvasChrome')
  return true
}

// el.call('openCanvasEntity', key) — navigate to the entity's own surface.
// A ref with no route (a record whose module did not stamp `meta.href`, a
// canvas ref) is a no-op by contract, never a guess at a URL.
export function openCanvasEntity(key) {
  const ref = this.call('parseEntityRef', key)
  if (!ref) return false
  // A module stamps its own route on the ROW (`data.href`); that beats any
  // per-type default this package could invent — see entityRefHref.
  const entry = this.call('getCanvasEntity', key)
  const rowHref = entry && entry.data && entry.data.data && entry.data.data.href
  const href = this.call('entityRefHref', rowHref ? { ...ref, meta: { href: rowHref } } : ref)
  if (!href) return false
  this.router(href, this.getRoot())
  return true
}

// el.call('fetchCanvasEntityRelations', key, opts) →
//   { ref, neighbors: [{ ref, relation, direction }] }
// Always returns that shape; an absent route or a failed call yields an empty
// neighbour list, which Expand treats as "nothing to add".
export async function fetchCanvasEntityRelations(key, opts) {
  const sdk = _canvasSdk(this)
  const res = await _execute(sdk, 'refs', 'relations', {
    ref: key,
    depth: (opts && opts.depth) || 1
  })
  const neighbors = (res && Array.isArray(res.neighbors) && res.neighbors) || []
  return { ref: key, neighbors }
}

// el.call('expandCanvasEntity', key, opts) — spec §3.2.
//
// One patch, not three: items + connections land together so the board never
// paints an arrow to a node that has not been placed yet.
//
// Connections are computed over ALL neighbours, placements only over the ones
// layoutEntityFan actually placed — a neighbour already on the board still
// gets its arrow, it just does not move.
export async function expandCanvasEntity(key, opts) {
  const v2 = this.call('getCanvasV2')
  const focus = v2 && v2.items && v2.items[key]
  if (!focus) return { added: 0, linked: 0 }
  const { neighbors } = await this.call('fetchCanvasEntityRelations', key, opts)
  if (!neighbors.length) return { added: 0, linked: 0 }
  const live = this.call('getCanvasV2') || v2
  const placed = this.call('layoutEntityFan', focus, neighbors, { taken: live.items || {} })
  const items = { ...(live.items || {}) }
  for (const p of placed) items[p.key] = { x: p.x, y: p.y, w: p.w, h: p.h }
  const connections = [...(live.connections || [])]
  let linked = 0
  for (const n of neighbors) {
    const nk = this.call('formatEntityRef', n && n.ref)
    if (!nk || nk === key || !items[nk]) continue
    const relation = (n && n.relation) || 'relates_to'
    const from = n && n.direction === 'in' ? nk : key
    const to = n && n.direction === 'in' ? key : nk
    if (connections.some((c) => c.from === from && c.to === to && c.label === relation)) continue
    connections.push({ from, to, label: relation, kind: relation })
    linked++
  }
  this.call('patchCanvas', { items, connections })
  this.call('refreshCanvasChrome')
  this.call('persistCanvasLayout')
  for (const p of placed) this.call('resolveCanvasEntity', p.key)
  return { added: placed.length, linked }
}

// el.call('searchCanvasEntities', query) → [{ ref, title, snippet, surface }]
// The picker's data source. Normalises both the bare-array and `{ data: [] }`
// response shapes, and drops any row without a usable ref.
export async function searchCanvasEntities(query) {
  const q = String(query || '').trim()
  if (!q) return []
  const sdk = _canvasSdk(this)
  const res = await _execute(sdk, 'search', 'workspace', { query: q, limit: 20 })
  const rows = Array.isArray(res) ? res : (res && Array.isArray(res.results) && res.results) || []
  const out = []
  for (const row of rows) {
    const key = this.call('formatEntityRef', row && row.ref)
    if (!key) continue
    out.push({
      key,
      ref: row.ref,
      title: _first(row.title, key),
      snippet: _clip(row.snippet, 160),
      surface: row.surface || ''
    })
  }
  return out
}

// el.call('boardEntityItems') → the entity nodes on the ACTIVE board page, in
// visual order (top-to-bottom, then left-to-right). Drives the sidebar's Items
// section. Deterministic — the same board always lists in the same order.
export function boardEntityItems() {
  const v2 = this.call('getCanvasV2')
  const items = (v2 && v2.items) || {}
  const rows = []
  for (const key of Object.keys(items)) {
    if (!this.call('isEntityRef', key)) continue
    const it = items[key] || {}
    const entry = this.call('getCanvasEntity', key)
    const ref = this.call('parseEntityRef', key)
    rows.push({
      id: key,
      key,
      x: Number(it.x) || 0,
      y: Number(it.y) || 0,
      title: (entry && entry.data && entry.data.title) || (ref && ref.id) || key,
      icon: this.call('entityRefIcon', key)
    })
  }
  rows.sort((a, b) => a.y - b.y || a.x - b.x || (a.key < b.key ? -1 : 1))
  return rows
}

// Open a transient page def in the primary PageSlot pane.
//
// The `root._aiAnswerPages` registry is PageSlot's documented escape hatch for
// a def that exists only for this session (components/PageSlot.js `_defFor`
// resolution order, step 1) — the same mechanism functions/answerUi.js uses.
// Reusing it is what lets an entity sheet be a real slot (back/forward,
// minimise, split) instead of a bespoke overlay.
const _openEntitySheet = (el, route, title, def) => {
  const root = _canvasRoot(el)
  if (!root || typeof root.update !== 'function') return false
  const entries = Object.entries(root._aiAnswerPages || {}).slice(-11)
  entries.push([route, { ...def, metadata: { ...(def.metadata || {}), title } }])
  root.update(
    {
      _aiAnswerPages: Object.fromEntries(entries),
      _canvasSlot: route,
      _slotFullscreen: null
    },
    { preventFetch: true }
  )
  return true
}

// A route is an opaque slot id — the ref is encoded so two different entities
// never collide on one sheet, and re-opening the same entity reuses its route.
const _sheetRoute = (prefix, key) => prefix + encodeURIComponent(key)

// el.call('openCanvasEntityComments', key) — spec §3.5.
//
// Mounts the shared `CommentThread` by BARE KEY. The component lives in
// `@symbo-ls/workspace-shared/components/CommentThread.js` and reaches this
// package through the same shared-library merge every other bare key here
// travels on (sharedLibraries.js), so the 3-tier resolver finds it with no
// import and no registration of our own.
//
// `entityRef` is the ONLY binding it needs: the thread keys on the ref, not on
// the canvas, which is why the same component also serves a training record or
// a document outside any board.
//
// `canManageOthers` is deliberately NOT passed. It mirrors the server's
// row-level gate (an editor or above may touch someone else's comment), and
// this package cannot resolve the viewer's workspace role — a guess would
// either hide a button the server allows or offer one it refuses. Unset, the
// thread offers delete on the viewer's OWN comments only, which is the true
// answer for a plain member and the safe one for everybody else. A surface
// that DOES know the viewer's role passes the prop at its own mount site.
export function openCanvasEntityComments(key) {
  const ref = this.call('parseEntityRef', key)
  if (!ref) return false
  const entry = this.call('getCanvasEntity', key)
  const title = (entry && entry.data && entry.data.title) || ref.id
  return _openEntitySheet(this, _sheetRoute('/entity-comments/', key), title, {
    flow: 'y',
    gap: 'A',
    padding: 'B',
    Heading: { tag: 'h2', fontSize: 'B', color: 'title', text: title },
    CommentThread: { entityRef: ref, flex: '1', minHeight: '0' }
  })
}

// el.call('makeCanvasEntityVariation', key) — spec §3.6.
//
// Creates a sibling row in the SAME collection carrying `data.variationOf`,
// places it east of the original with a `variation_of` connection, then opens
// the editable sheet. Records only: a variation of a document or an email is
// not a thing this package can define.
export async function makeCanvasEntityVariation(key) {
  const ref = this.call('parseEntityRef', key)
  if (!ref || ref.type !== 'record') return null
  const v2 = this.call('getCanvasV2')
  const focus = v2 && v2.items && v2.items[key]
  if (!focus) return null
  const sdk = _canvasSdk(this)
  if (!sdk) return null
  const entry = (await this.call('resolveCanvasEntity', key)) || {}
  const source = entry.data || {}
  const slash = ref.id.indexOf('/')
  const collection = slash > 0 ? ref.id.slice(0, slash) : ref.collection || ''
  const title = this.call('polyglot', 'canvas.entity.variation.title').replace(
    '{title}',
    source.title || ref.id
  )
  const created = await _execute(sdk, 'workspaceProject.records', 'create', {
    collection,
    name: title,
    data: { ...(source.data || {}), title, variationOf: key, rationale: '' }
  })
  const newId = created && (created._id || created.id)
  if (!newId) return null
  const newKey = this.call('formatEntityRef', {
    type: 'record',
    id: collection ? collection + '/' + newId : String(newId)
  })
  if (!newKey) return null
  const live = this.call('getCanvasV2') || v2
  const items = {
    ...(live.items || {}),
    [newKey]: { x: focus.x + focus.w + 120, y: focus.y, w: 280, h: 160 }
  }
  // The relation NAME is both the edge's `kind` and its rendered label, and it
  // is never translated: it is platform vocabulary (spec §2), the same
  // identifier the relations endpoint returns for every OTHER edge on the
  // board. Translating the one edge this package mints locally would make it
  // read differently from its identical server-supplied neighbours. Named once
  // here for the same reason expandCanvasEntity names its own `relation`.
  const relation = 'variation_of'
  const connections = [
    ...(live.connections || []),
    { from: newKey, to: key, label: relation, kind: relation }
  ]
  this.call('patchCanvas', { items, connections })
  this.call('refreshCanvasChrome')
  this.call('persistCanvasLayout')
  await this.call('resolveCanvasEntity', newKey)
  this.call('openCanvasEntityEditor', newKey)
  return newKey
}

// el.call('openCanvasEntityEditor', key) — the editable title/body/rationale
// form the variation flow lands on. Saves through `records update`.
//
// The form is a transient slot def (see _openEntitySheet), so its handlers are
// runtime data rather than an emitted component — the same shape answerUi.js
// mounts. Every field is controlled from the slot's own state; nothing writes
// `node.value` (FA511).
export function openCanvasEntityEditor(key) {
  const ref = this.call('parseEntityRef', key)
  if (!ref) return false
  const entry = this.call('getCanvasEntity', key)
  const cur = (entry && entry.data) || {}
  const heading = cur.title || ref.id
  // `labelTemplate` is the FULL polyglot template, never a key concatenated
  // into one: `text:` needs the TEMPLATE form (docs/I18N-CONVENTION.md §1) and
  // a key assembled with `+` is invisible to the extraction audit.
  const field = (labelTemplate, stateKey, tag) => ({
    flow: 'y',
    gap: 'V',
    Label: {
      tag: 'span',
      fontSize: 'Z',
      color: 'caption',
      text: labelTemplate
    },
    Input: {
      tag: tag || 'input',
      type: tag ? null : 'text',
      width: '100%',
      minHeight: tag ? 'E' : null,
      padding: 'Z A',
      fontSize: 'Z1',
      color: 'title',
      background: 'overlayStrong',
      borderWidth: '0',
      round: 'radiusControl',
      value: (el, s) => (s.form && s.form[stateKey]) || '',
      onInput: (e, el, s) => s.update({ form: { ...(s.form || {}), [stateKey]: el.node.value } })
    }
  })
  return _openEntitySheet(this, _sheetRoute('/entity-editor/', key), heading, {
    state: {
      entityKey: key,
      form: {
        title: cur.title || '',
        body: (cur.data && cur.data.body) || '',
        rationale: (cur.data && cur.data.rationale) || ''
      }
    },
    flow: 'y',
    gap: 'A',
    padding: 'B',
    Title: field('{{ canvas.entity.variation.titleLabel | polyglot }}', 'title'),
    Rationale: field('{{ canvas.entity.variation.rationaleLabel | polyglot }}', 'rationale'),
    Body: field('{{ canvas.entity.variation.bodyLabel | polyglot }}', 'body', 'textarea'),
    SaveBtn: {
      extends: 'Button',
      theme: 'primary',
      type: 'button',
      margin: 'V 0 0',
      text: '{{ common.action.save | polyglot }}',
      onClick: (e, el, s) => {
        e.preventDefault()
        el.call('saveCanvasEntityEdits', s.entityKey, s.form)
      }
    }
  })
}

// el.call('saveCanvasEntityEdits', key, form) — the editor's write half.
// Records only (the one type this package knows how to write).
export async function saveCanvasEntityEdits(key, form) {
  const ref = this.call('parseEntityRef', key)
  if (!ref || ref.type !== 'record') return false
  const sdk = _canvasSdk(this)
  if (!sdk) return false
  const slash = ref.id.indexOf('/')
  const id = slash > 0 ? ref.id.slice(slash + 1) : ref.id
  const entry = this.call('getCanvasEntity', key)
  const prev = (entry && entry.data && entry.data.data) || {}
  const patch = form || {}
  const res = await _execute(sdk, 'workspaceProject.records', 'update', {
    id,
    name: patch.title || (entry && entry.data && entry.data.title) || '',
    data: { ...prev, title: patch.title, body: patch.body, rationale: patch.rationale }
  })
  if (!res) return false
  this.call('patchCanvasEntity', key, { loading: false, error: null, data: null })
  await this.call('resolveCanvasEntity', key)
  return true
}
